<template>
  <!-- prettier-ignore -->
  <div
    ref="priceWidgetRef"
    class="price-widget-wrapper"
    :app-version="version$"
    :app-name="appName$"
    :app-language="selectedLanguage"
  >
    <template v-if="!isReady">
      <widget-loading/>
    </template>

    <template v-else>

      <template v-if="isLoginEnabled">
        <login-action
          ref="loginRef"
          class="pwLogin"
          :channel="CHANNEL.PRICEWIDGET"
          :product-id="$props.productId"
        />
      </template>

      <div
        :id="`priceWidget_${$props.productId}`"
        :class="pwClasses"
        :data-product-id="$props.productId"
      >
        <div
          class="price-widget-tabs"
        >
          <template
            v-for="tab in tabs"
            :key="tab.id"
          >
            <the-typography
              type="bodyLargeShort"
              data-track-as="pricewidget-tabs"
              :data-track-text="tab.id"
              class="price-widget-tabs__tab"
              :class="{ 'price-widget-tabs__tab--active': activeTabId === tab.id }"
              @click="select(tab.id)"
            >
              <span v-text="$t(tab.title)" />
              <the-icon
                art="light"
                name="angle-down"
              />
            </the-typography>
            <template v-if="!browser.isDesktop">
              <div
                v-show="activeTabId === tab.id"
                :key="`container-${tab.id}`"
                class="price-widget-content"
              >
                <component
                  :is="tab.component"
                  :product-id="$props.productId"
                  :source="source"
                />
              </div>
            </template>
          </template>
        </div>


        <template v-if="browser.isDesktop">
          <div
            class="price-widget-content"
          >
            <template
              v-for="tab in tabs"
              :key="tab.id"
            >
              <component
                :is="tab.component"
                v-show="activeTabId === tab.id"
                :product-id="$props.productId"
                :source="source"
              />
            </template>
          </div>
        </template>
      </div>
    </template>

    <the-message
      v-if="showMessage"
      class="price-widget-message"
      :text="$t('temp.navigation.disabled')"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, inject } from 'vue'

import { SortUtil } from '@/utils/Sorting'
import { useApplication, useBasket, useBrowser, useCollective, useI18n, usePartner } from '@/hooks'

import AdviceComponent from './PriceWidgetAdvice.vue'
import OfferComponent from './PriceWidgetOffer.vue'

import { LoginAction } from '@/components/Login'
import WidgetLoading from '@/components/Container/WidgetLoading'
import TheMessage from '@/components/Container/TheMessage'

import { SOURCE } from '@/config/events'
import { CHANNEL, COLLECTIVE_CHANNEL, DISTRIBUTION_PARTNER_WITH_ADVISOR_MAIL } from '@/config/constants'

// HOOKS
const version$ = inject('version$')
const appName$ = inject('appName$')
const { isReady } = useApplication()
const { basket, isStaticBasket } = useBasket()
const { browser } = useBrowser()
const { collective } = useCollective()
const { selectedLanguage } = useI18n()
const { partner, isPartner } = usePartner()

// Init
defineProps({
  productId: {
    type: String,
    required: true,
  },

  source: {
    type: String,
    default: SOURCE.PRICE_WIDGET,
  },
})

// DATA
const activeTabId = ref(undefined)
const priceWidgetRef = ref(undefined)
const loginRef = ref(null)

// COMPUTED
const hasPartnerWithAdvisorEmail = computed(() => {
  const { masterTreaty } = basket
  if (!masterTreaty) return false

  return isPartner.value && DISTRIBUTION_PARTNER_WITH_ADVISOR_MAIL.includes(masterTreaty)
})

const isLoginEnabled = computed(() => {
  return (
    (basket.channel !== CHANNEL.ALVA &&
      hasPartnerWithAdvisorEmail.value === false &&
      partner.value.isBroker === false &&
      (basket.collective === undefined || basket.collective === null) &&
      !basket.existingCustomer) ||
    !!loginRef.value?.isLoginModalOpen
  )
})

const showMessage = computed(() => {
  return isReady.value && !!isStaticBasket.value
})

const hideAide = computed(() => {
  return (
    collective?.channel === COLLECTIVE_CHANNEL.SCPC ||
    basket.existingCustomer ||
    partner.value.isBroker ||
    partner.value.isMobileDistributor
  )
})

const tabs = computed(() => {
  const tabEntries = [
    {
      component: OfferComponent,
      id: 'widget',
      order: 0,
      title: 'screen.priceWidget.widget.title',
    },
  ]

  if (!hideAide.value || !basket.existingCustomer) {
    tabEntries.push({
      component: AdviceComponent,
      id: 'advice',
      order: 1,
      title: 'screen.priceWidget.advice.title',
    })
  }

  return tabEntries.sort(SortUtil.sortByOrder)
})

const pwClasses = computed(() => {
  return {
    'price-widget': true,
    'price-widget-blur': showMessage.value,
  }
})

// METHODS
/**
 * select tab
 *
 * @param {Number} tabId   Tab id
 * @param {Boolean} scroll   scroll tab into view? default: true
 */
function select(tabId, scroll = true) {
  activeTabId.value = tabId

  if (scroll) {
    const el = priceWidgetRef.value.querySelector(`.price-widget-tabs__tab[data-track-text='${tabId}']`)
    if (browser.isDesktop) {
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    } else {
      nextTick(() => {
        const contentHeight = el.nextSibling.offsetHeight

        let scrollToValue
        if (window.innerHeight - contentHeight <= 300) {
          scrollToValue = el.offsetTop + el.nextSibling.offsetHeight * 2
        } else {
          scrollToValue = el.offsetTop + el.nextSibling.offsetHeight * 2 + 650
        }

        window.scroll(0, scrollToValue)
      })
    }
  }
}

// Lifecycle Hooks
onMounted(() => {
  if (!activeTabId.value) {
    select(tabs.value[0].id, false)
  }
})
</script>

<style name="mobile" scoped>
.price-widget-wrapper {
  position: relative;
}

.price-widget-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  border: 1px solid var(--c-secondary-neutral-3);
}

.price-widget-blur {
  filter: blur(16px);
  pointer-events: none;
}

.pwLogin {
  margin: 0 0 16px 0;
}

.price-widget {
  &-tabs {
    display: flex;
    flex-direction: column;

    &__tab {
      padding: 10px 0;
      border: 1px solid var(--c-primary-neutral-1);
      text-align: center;
      transition: all 0.3s ease-in-out;
      margin: 0 0 10px 0;
      position: relative;

      .icon {
        font-size: 16px;
        position: absolute;
        top: 15px;
        right: 20px;
      }

      &--active {
        background-color: var(--c-primary-color-3);
        border-color: var(--c-primary-color-3);
        color: var(--c-primary-neutral-3);
        font-weight: bold;
        position: relative;
        margin: 0 0 0 0;

        &:after {
          content: '';
          position: absolute;
          bottom: -11px;
          left: calc(50% - 5px);
          z-index: 1;

          width: 0px;
          height: 0px;
          border-right: 10px solid transparent;
          border-left: 10px solid transparent;
          border-top: 10px solid var(--c-primary-color-3);
        }
      }
    }
  }

  &-content {
    margin: 0 0 20px 0;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .price-widget {
    position: relative;
    display: grid;
    grid-template-columns: 240px 1fr;
  }

  .price-widget-tabs {
    display: grid;
    grid-gap: 15px;
    grid-template-rows: repeat(auto-fill, 40px);
  }

  .price-widget-tabs__tab {
    height: 40px;
    width: 220px;
    border: 1px solid var(--c-primary-neutral-1);
    padding: 10px 0;
    margin: 0;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease-in-out;

    .icon {
      display: none;
    }

    &:hover {
      background-color: var(--c-primary-color-3);
      color: var(--c-primary-neutral-3);
    }
  }

  .price-widget-tabs__tab--active {
    background-color: var(--c-primary-color-3);
    border-color: var(--c-primary-color-3);
    color: var(--c-primary-neutral-3);
    font-weight: bold;
    position: relative;

    &:after {
      content: '';
      position: absolute;
      bottom: initial;
      left: initial;
      top: 8px;
      right: -10px;

      width: 0px;
      height: 0px;
      border-right: 0;
      border-top: 10px solid transparent;
      border-bottom: 10px solid transparent;
      border-left: 10px solid var(--c-primary-color-3);
    }
  }
}
</style>
